import { colors, radius } from '@ardoq/design-tokens';
import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { ModalSize } from '../types';

export interface ModalContainerProps {
  modalSize?: ModalSize;
  fixedHeight?: boolean | string;
  children?: ReactNode;
  className?: string;
}

const getCustomHeightCss = (customHeight: string | boolean) =>
  typeof customHeight === 'string' ? `height: ${customHeight};` : '';

const DEFAULT_FIXED_HEIGHT = {
  [ModalSize.XS]: 'min-height: 240px;',
  [ModalSize.S]: 'height: 63vh;',
  [ModalSize.M]: 'height: 71vh;',
  [ModalSize.L]: 'height: 93vh;',
};

const containerSizeProvider = ({
  modalSize,
  fixedHeight = false,
}: Pick<ModalContainerProps, 'modalSize' | 'fixedHeight'>) => {
  if (modalSize === ModalSize.XS) {
    return css`
      width: 340px;
      min-height: 240px;
      max-height: 43vh;
      ${fixedHeight === true
        ? DEFAULT_FIXED_HEIGHT[ModalSize.XS]
        : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.S) {
    return css`
      width: 500px;
      min-height: 240px;
      max-height: 63vh;
      ${fixedHeight === true
        ? DEFAULT_FIXED_HEIGHT[ModalSize.S]
        : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.M) {
    return css`
      width: 900px;
      min-height: 240px;
      max-height: 71vh;
      ${fixedHeight === true
        ? DEFAULT_FIXED_HEIGHT[ModalSize.M]
        : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.L) {
    return css`
      min-height: 240px;
      max-height: 93vh;

      width: 93vh;
      min-width: 1000px;
      max-width: 1440px;
      ${fixedHeight === true
        ? DEFAULT_FIXED_HEIGHT[ModalSize.L]
        : getCustomHeightCss(fixedHeight)}
    `;
  } else if (modalSize === ModalSize.COVER) {
    return css`
      width: 100vw;
      height: 100vh;
    `;
  }
  return css``;
};

const ModalContainerStyle = styled.div<{
  $modalSize: ModalSize;
  $fixedHeight: string | boolean;
}>`
  display: flex;
  flex-direction: column;
  border-radius: ${radius.r8};
  background-color: ${colors.surfaceDefault};
  ${({ $modalSize, $fixedHeight }) =>
    containerSizeProvider({
      modalSize: $modalSize,
      fixedHeight: $fixedHeight,
    })};
`;

const ModalContainer = ({
  children,
  modalSize = ModalSize.XS,
  fixedHeight = false,
  className,
}: ModalContainerProps) => (
  <ModalContainerStyle
    $modalSize={modalSize}
    $fixedHeight={fixedHeight}
    className={className}
  >
    {children}
  </ModalContainerStyle>
);

export const MODAL_CONTAINER_SELECTOR = ModalContainerStyle.toString();

export { ModalContainer };
